/**
 * The feed reads its filters off the URL, and a shared or hand-edited link can
 * carry values no control would ever produce. Reading them raw made an
 * unknown ordering render a blank sort button and no heading, the string
 * "false" read as an active boolean filter (it is truthy), and a malformed
 * category list claim selections that do not exist. Sanitizing at the read
 * site means every consumer — heading, dropdowns, the questions request —
 * agrees on what the URL actually asks for.
 */

export const FEED_ORDERINGS = ['-created_on', 'bet_end_date', '-wagers_count', '-volume', '-relevance']

const BOOLEAN_FILTERS = [
  'resolved_only',
  'pending_resolution',
  'live',
  'live_soon',
  'resolved_and_sold',
  'hide_my_bets',
  'only_markets_i_follow',
  'people_i_follow',
]

export const isValidFeedOrdering = value => FEED_ORDERINGS.includes(value)

/** Only an explicit true is on; "false" (and anything else) is off. */
export const isTruthyFeedParam = value => value === true || value === 'true'

const sanitizeCategories = value => {
  if (typeof value !== 'string') return value
  const ids = [...new Set(value.split(',').filter(id => /^\d+$/.test(id) && Number(id) > 0))]
  return ids.length > 0 ? ids.join(',') : undefined
}

export function sanitizeFeedQueryParams(params = {}) {
  const clean = { ...params }

  if ('ordering' in clean && !isValidFeedOrdering(clean.ordering)) delete clean.ordering

  for (const key of BOOLEAN_FILTERS) {
    if (!(key in clean)) continue
    if (isTruthyFeedParam(clean[key])) clean[key] = 'true'
    else delete clean[key]
  }

  if ('categories' in clean) {
    const categories = sanitizeCategories(clean.categories)
    if (categories === undefined) delete clean.categories
    else clean.categories = categories
  }

  /* A topic is only ever compared against a featured category's slug or id,
     never rendered, so it needs no character allowlist — unicode slugs from
     the API must survive. Only the shape that could break the URL or the
     comparison (empty, overlong, path/query separators) is dropped. */
  if ('topic' in clean) {
    const topic = String(clean.topic)
    if (!topic || topic.length > 100 || /[\s/?#]/.test(topic)) delete clean.topic
  }

  return clean
}

/** Whether sanitizing changed anything — the caller canonicalizes the URL when it did. */
export function feedQueryParamsChanged(before = {}, after = {}) {
  const beforeKeys = Object.keys(before)
  if (beforeKeys.length !== Object.keys(after).length) return true
  return beforeKeys.some(key => String(before[key]) !== String(after[key]))
}
